import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Calendar, Car, Ban, AlertCircle, Clock
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVehicleBlocks = () => {
  const { token } = useAuth();
  const [blocks, setBlocks] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [vehicleFilter, setVehicleFilter] = useState('all');
  const [showPast, setShowPast] = useState(false);
  const [minimumGapHours, setMinimumGapHours] = useState(4);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState(null);
  
  const [formData, setFormData] = useState({
    vehicle_id: '',
    start_date: '',
    end_date: '',
    reason: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBlocks = async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/admin/vehicle-blocks?active_only=${!showPast}`, 
        { headers }
      );
      setBlocks(response.data);
    } catch (error) {
      console.error('Error fetching blocks:', error);
      toast.error('Échec du chargement des blocages');
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/vehicles`, { headers });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/settings/booking`, { headers });
      setMinimumGapHours(response.data.minimum_gap_hours || 4);
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  useEffect(() => {
    fetchVehicles();
    fetchBlocks();
    fetchSettings();
  }, [showPast]);

  const openCreateDialog = () => {
    setFormData({
      vehicle_id: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_id || !formData.start_date || !formData.end_date) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      await axios.post(`${API_URL}/api/admin/vehicle-blocks`, formData, { headers });
      toast.success('Vehicle blocked successfully');
      setDialogOpen(false);
      fetchBlocks();
    } catch (error) {
      console.error('Error creating block:', error);
      const message = error.response?.data?.detail || 'Failed to create block';
      toast.error(message);
    }
  };

  const handleDelete = async () => {
    if (!selectedBlock) return;
    
    try {
      await axios.delete(`${API_URL}/api/admin/vehicle-blocks/${selectedBlock.id}`, { headers });
      toast.success('Block removed successfully');
      setDeleteDialogOpen(false);
      setSelectedBlock(null);
      fetchBlocks();
    } catch (error) {
      console.error('Error deleting block:', error);
      toast.error('Failed to remove block');
    }
  };

  const filteredBlocks = blocks.filter(b => {
    if (vehicleFilter === 'all') return true;
    return b.vehicle_id === vehicleFilter;
  });

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const isBlockActive = (block) => {
    const today = new Date().toISOString().split('T')[0];
    return block.start_date <= today && block.end_date >= today;
  };

  const isBlockFuture = (block) => {
    const today = new Date().toISOString().split('T')[0];
    return block.start_date > today;
  };

  return (
    <div className="space-y-6" data-testid="admin-vehicle-blocks">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl text-[#181B1F] font-light">Blocages</h1>
          <p className="text-gray-500 mt-1">Bloquer des véhicules pour les réservations en ligne</p>
        </div>
        <Button onClick={openCreateDialog} className="btn-primary" data-testid="add-block-btn">
          <Plus className="w-4 h-4 mr-2" />
          Bloquer un véhicule
        </Button>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-800 text-sm">
            Les véhicules bloqués n'apparaîtront pas dans les résultats de recherche pendant la période bloquée.
          </p>
          <p className="text-amber-700/80 text-xs mt-1 flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Délai minimum entre deux événements : <strong>{minimumGapHours} heures</strong> (configurable dans Paramètres)
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
          <SelectTrigger className="input-default w-60" data-testid="filter-vehicle">
            <SelectValue placeholder="Filtrer par véhicule" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-[#181B1F]">Tous les véhicules</SelectItem>
            {vehicles.map(v => (
              <SelectItem key={v.id} value={v.id} className="text-[#181B1F]">
                {v.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <label className="flex items-center gap-2 text-gray-500 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={showPast}
            onChange={(e) => setShowPast(e.target.checked)}
            className="rounded border-gray-300"
          />
          Afficher les blocages passés
        </label>
      </div>

      {/* Blocks List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Chargement des blocages...</p>
        </div>
      ) : filteredBlocks.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-100 rounded-sm">
          <Ban className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Aucun blocage trouvé</p>
          <p className="text-gray-400 text-sm mt-1">
            Bloquez un véhicule pour empêcher les réservations en ligne pendant une période
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredBlocks.map((block, index) => (
            <motion.div
              key={block.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="bg-white border border-gray-100 rounded-sm p-4"
              data-testid={`block-item-${block.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-red-500/10 rounded-sm flex items-center justify-center">
                    <Ban className="w-5 h-5 text-red-400" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="text-[#181B1F] font-medium">{block.vehicle_name}</h3>
                      {isBlockActive(block) && (
                        <Badge className="bg-red-500/80 text-xs">En cours</Badge>
                      )}
                      {isBlockFuture(block) && (
                        <Badge className="bg-blue-500/80 text-xs">À venir</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {formatDate(block.start_date)} – {formatDate(block.end_date)}
                      </span>
                      {(block.reason || block.note) && (
                        <span className="text-gray-400">• {block.reason || block.note}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-400 text-xs">
                    par {block.created_by}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setSelectedBlock(block);
                      setDeleteDialogOpen(true);
                    }}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-block-${block.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Create Block Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#181B1F]">
              Bloquer un véhicule
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-gray-500">Véhicule *</Label>
              <Select 
                value={formData.vehicle_id} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, vehicle_id: v }))}
              >
                <SelectTrigger className="input-default" data-testid="select-vehicle">
                  <SelectValue placeholder="Sélectionner un véhicule" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  {vehicles.map(v => (
                    <SelectItem key={v.id} value={v.id} className="text-[#181B1F]">
                      {v.name} ({v.brand})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-gray-500">Date de début *</Label>
                <Input
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-500">Date de fin *</Label>
                <Input
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  className="input-default"
                  required
                  data-testid="input-end-date"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-500">Raison (optionnel)</Label>
              <Textarea
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className="input-default"
                placeholder="ex: Maintenance, Prêté pour réparations, Location externe..."
                rows={2}
                data-testid="input-reason"
              />
            </div>

            <div className="bg-amber-500/10 border border-amber-500/20 rounded-sm p-3 text-sm">
              <p className="text-amber-800">
                Le système vérifie automatiquement qu'il n'y a pas de conflit avec les réservations existantes et respecte le délai minimum de {minimumGapHours}h entre deux événements.
              </p>
            </div>

            <div className="flex justify-end gap-4 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)} 
                className="btn-outline"
              >
                Annuler
              </Button>
              <Button type="submit" className="btn-primary" data-testid="submit-block">
                Bloquer le véhicule
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#181B1F]">Supprimer le blocage</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Êtes-vous sûr de vouloir supprimer ce blocage pour "{selectedBlock?.vehicle_name}" ?
              <br />
              <span className="text-gray-400">
                Période : {selectedBlock && formatDate(selectedBlock.start_date)} – {selectedBlock && formatDate(selectedBlock.end_date)}
              </span>
              <br /><br />
              Le véhicule redeviendra disponible pour les réservations en ligne.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="btn-outline">Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-red-500 hover:bg-red-600 text-white"
              data-testid="confirm-delete-block"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminVehicleBlocks;
